import { eventDetails, eventTarget } from './eventDetails';
import useEvents, { EVENTS_PER_PAGE } from './useEvents';
import Pill from '../shared/Pill';
import { eventLabel } from '../shared/labels';
import { formatDateTime } from '../shared/format';
import SectionState from '../shared/SectionState';
import { useLifecycleUrlState } from '../useLifecycleUrlState';
import { Scroll } from '../shared/Scroll';

import Button from '@/components/Button';
import { Small } from '@/components/Typography';
import styled from '@/styles';

const FILTER_OPTIONS = [
  'journey_settings_changed',
  'sms_paused',
  'sms_resumed',
  'copy_published',
  'copy_reverted',
  'copy_reset',
];

const TONES: Record<string, 'grey' | 'amber' | 'green' | 'blue'> = {
  journey_settings_changed: 'grey',
  sms_paused: 'amber',
  sms_resumed: 'green',
  copy_published: 'blue',
  copy_reverted: 'blue',
  copy_reset: 'blue',
};

const Frame = styled('section', {
  backgroundColor: '#FFFFFF',
  border: '1px solid #DADADA',
  borderRadius: '8px',
});

const Toolbar = styled('div', {
  display: 'flex',
  flexWrap: 'wrap',
  gap: '12px',
  alignItems: 'center',
  marginBottom: '20px',
});

const FilterLabel = styled('label', {
  display: 'flex',
  gap: '6px',
  alignItems: 'center',
  color: '#3D3D3D',
  fontSize: '13px',
});

const Select = styled('select', {
  font: 'inherit',
  fontSize: '14px',
  border: '1px solid #BDBDBD',
  borderRadius: '6px',
  padding: '8px 10px',
  backgroundColor: '#FFFFFF',
  color: '#1E1E1E',
});

const Box = styled('div', { padding: '0 20px 20px' });

const Row = styled('div', {
  display: 'grid',
  gridTemplateColumns: '170px 220px 190px 150px minmax(280px, 2fr)',
  gap: '12px',
  alignItems: 'start',
  padding: '12px 16px',
  borderTop: '1px solid #EDEDED',
  minWidth: '1080px',
  fontSize: '14px',
  variants: {
    head: {
      true: {
        borderTop: 'none',
        backgroundColor: '#FAFAFA',
        fontSize: '12px',
        fontWeight: 600,
        color: '#5A5A5A',
        textTransform: 'uppercase',
        letterSpacing: '.03em',
      },
    },
  },
});

const Stacked = styled('span', {
  display: 'flex',
  flexDirection: 'column',
  gap: '2px',
  alignItems: 'flex-start',
});

const Footer = styled('div', {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: '12px',
  padding: '12px 16px',
  borderTop: '1px solid #EDEDED',
});

const Pager = styled('span', {
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
});

const EventsTab = () => {
  const { params, update } = useLifecycleUrlState<'events'>();
  const { events, pagination, loading, error, retry } = useEvents(
    params.event_key,
    params.page,
  );

  const totalPages = Math.max(pagination?.total_pages ?? 1, 1);
  const total = pagination?.total ?? events.length;

  const renderBody = () => {
    if (loading) return <SectionState mode="loading" title="system events" />;
    if (error) {
      return (
        <>
          <SectionState
            mode="error"
            error={error}
            title="system events"
            onRetry={retry}
          />
          {params.event_key && (
            <Box>
              <Button
                a11yLabel="Show all events"
                label="Show all events"
                appearance="outline"
                size="sm"
                onClick={() => update({ event_key: null }, { replace: true })}
              />
            </Box>
          )}
        </>
      );
    }
    if (events.length === 0 && params.page > 1) {
      return (
        <>
          <SectionState mode="empty" title="No events on this page." />
          <Box>
            <Button
              a11yLabel="Go to page 1"
              label="Go to page 1"
              appearance="outline"
              size="sm"
              onClick={() => update({ page: 1 })}
            />
          </Box>
        </>
      );
    }
    if (events.length === 0) {
      return (
        <SectionState
          mode="empty"
          title="No system events yet"
          caption="Setting changes, automatic SMS pauses and copy publishes appear here."
        />
      );
    }
    // Only the rows scroll sideways; the pager stays below them at the
    // screen's width, so "Next" is in reach on a 1280px laptop (review RV-11).
    return (
      <>
        <Scroll>
          <Row head>
            <span>Time</span>
            <span>Event</span>
            <span>Journey or message</span>
            <span>Who</span>
            <span>Details</span>
          </Row>
          {events.map((event) => (
            <Row key={event.id} data-testid={`systemEventRow-${event.id}`}>
              <span>{formatDateTime(event.occurred_at)}</span>
              <Stacked css={{ gap: '4px' }}>
                <Pill tone={TONES[event.event_key] ?? 'grey'}>
                  {eventLabel(event.event_key)}
                </Pill>
                <Small color="light">{event.event_key}</Small>
              </Stacked>
              <span>{eventTarget(event)}</span>
              <span>{event.actor_name || 'System'}</span>
              <Stacked>
                {eventDetails(event).map((line, index) => (
                  <span key={`${index}-${line}`}>{line}</span>
                ))}
              </Stacked>
            </Row>
          ))}
        </Scroll>
        <Footer>
          <Small color="light">{`${total} ${total === 1 ? 'event' : 'events'}`}</Small>
          <Pager>
            <Button
              a11yLabel="Previous page"
              label="Previous"
              appearance="outline"
              size="sm"
              disabled={params.page <= 1}
              onClick={() => update({ page: params.page - 1 })}
            />
            <Small color="light">{`Page ${params.page} of ${totalPages} · ${EVENTS_PER_PAGE} per page`}</Small>
            <Button
              a11yLabel="Next page"
              label="Next"
              appearance="outline"
              size="sm"
              disabled={params.page >= totalPages}
              onClick={() => update({ page: params.page + 1 })}
            />
          </Pager>
        </Footer>
      </>
    );
  };

  return (
    <div>
      <Toolbar>
        <FilterLabel>
          Event
          <Select
            data-testid="systemEventFilter"
            value={params.event_key ?? ''}
            onChange={(e) =>
              update({ event_key: e.target.value || null }, { replace: true })
            }
          >
            <option value="">All events</option>
            {FILTER_OPTIONS.map((key) => (
              <option key={key} value={key}>
                {eventLabel(key)}
              </option>
            ))}
          </Select>
        </FilterLabel>
        <Small color="light">
          Changes to the engine and journeys, newest first. Worker events are on
          each worker's Messaging tab.
        </Small>
      </Toolbar>
      <Frame data-testid="systemEventTable" aria-label="System events">
        {renderBody()}
      </Frame>
    </div>
  );
};

export default EventsTab;
